// Global
import React, { useCallback, useRef } from 'react';

// Local
import styles from './VerticalVideoGallery.module.scss';
import VerticalVideoCard from '../VerticalVideoCard/VerticalVideoCard';
import type { VerticalVideoCardData } from '../VerticalVideoCard/VerticalVideoCard';

export interface VerticalVideoGalleryProps {
  contentListName?: string;
  videos: VerticalVideoCardData[];
  onPlayVideo: (video: VerticalVideoCardData, trigger?: HTMLButtonElement) => void;
  ctaLinksLeftAligned?: boolean;
}

export default function VerticalVideoGallery({
  contentListName,
  videos,
  onPlayVideo,
  ctaLinksLeftAligned = true,
}: VerticalVideoGalleryProps) {
  const galleryScrollRef = useRef<HTMLUListElement>(null);

  // When a card receives focus, ensure it's fully visible within the scrollable gallery container
  const handleFocusCapture = useCallback((event: React.FocusEvent<HTMLUListElement>) => {
    const currentTarget = event.currentTarget;
    const focusTarget = event.target as HTMLElement;

    if (!currentTarget.contains(focusTarget)) {
      return;
    }

    const focusedCard = focusTarget.closest('[data-gallery-card]') as HTMLElement | null;
    if (!focusedCard) {
      return;
    }

    focusedCard.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, []);

  if (!videos || videos.length === 0) {
    return null;
  }

  const cardCount = Math.min(videos.length, 3);
  const limitedVideos = videos.slice(0, 3);

  return (
    <div className={styles.galleryWrapper} data-card-count={cardCount}>
      <ul
        ref={galleryScrollRef}
        className={styles.gallery}
        aria-label="Video gallery"
        onFocusCapture={handleFocusCapture}
      >
        {limitedVideos.map((video, index) => (
          <li
            key={`${video.videoId || video.videoUrl || 'video'}-${index}`}
            className={styles.galleryItem}
            data-gallery-card
          >
            <VerticalVideoCard
              video={video}
              onPlay={onPlayVideo}
              ctaLinksLeftAligned={ctaLinksLeftAligned}
              contentListName={contentListName}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
